import React, { createContext, useState, useEffect } from 'react';
import { AppearanceApi } from "../services/AppearanceApi";
import Router from "../router";
import { useTranslation } from 'react-i18next'

const ColorsContext = createContext();

const ColorsProvider = ({ children }) => {

  const { t } = useTranslation();

  const initialColors = {
    navbarColor: '#3DCD58',
    footerColor: '#3DCD58',
    sidebarSelectColor: '#3DCD58',
    sectionTitleColor: '#3DCD58',
    buttonActionColor: '#42B4E6',
    detailTextColor: '#53c3f5',
    titleTextColor: '#0c0b0c',
    main: '#42B4E6',
    mainClear: '#53c3f5',
    mainGreen: '#3DCD58',
    mainGreenClear: '#42e060',
    disabled: '#9fa4a9',
    disabledInput: '#f0f0f0',
    background: '#F5F5F5',
    grayFont: '#919191',
    grayGraphic: '#C3C3C3',
    blackFont: '#0d0c22',
    white: '#FFFFFF',
    gray: '#eaeaea',
    orange: '#ff9406',
    red: '#f44e5b',
    purple: '#99539b',
    green: '#4dd331',
    blue: '#21a2ea',
    notification: '#d2ebfa',
    darkOrange: '#E38A2D',
    default: '#000000de',
    grayDemo: '#C7C7C7',

    imageLoginUrl: '',
    iconUrl: '',
    loginIconUrl: '',
    faviconUrl: null,
    title: null
  }

  const getSavedColors = () => {
    try {
      const saved = localStorage.getItem('cenit-appearance-colors');
      return saved ? JSON.parse(saved) : initialColors;
    } catch (e) {
      return initialColors;
    }
  };

  const actionRef = React.useRef(false)
  const [colors, setColors] = useState(getSavedColors());
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [contentRender, setContentRender] = useState(false);
  const [flagNotification, setFlagNotification] = useState(false);

  const updateColors = (newColors) => {
    const updated = {...initialColors, ...newColors};
    setColors(updated);
    localStorage.setItem('cenit-appearance-colors', JSON.stringify(updated));
  };

  const resetColors = () => {
    setColors(initialColors);
    localStorage.removeItem('cenit-appearance-colors');
  };
  
  const updatePageTitle = (newTitle) => {
    let titleElement = document.getElementsByTagName('title')[0];
    if (titleElement) {
      titleElement.innerHTML = newTitle;
    } else {
      document.title = newTitle;
    }
  }

  //?? Renderizar contenido 
  const renderContent = () => {
    setTimeout(() => {
      setContentRender(true)
      setOpenBackDrop(false);
    }, 1000)
  }

  // Descargar imagen y convertirla a base64 para persistencia offline
  const downloadImageAsBase64 = async (relativePath) => {
    try {
      const fullUrl = `${Router.apiBaseUrl}/${relativePath}`;
      const response = await fetch(fullUrl);
      if (!response.ok) return null;
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const getAppearance = React.useCallback(async() => {
    try {
      const workflowDataApi = new AppearanceApi();
      setOpenBackDrop(true);
      let response = await workflowDataApi.get(false);
      
      if (response?.status === 200) {
        let data = response.data
        if (data?.isActive === true) {
          const savedColors = getSavedColors();
          const updatedColors = {...initialColors, ...data};
          
          if (navigator.onLine) {
            // Online: descargar y cachear imágenes como base64
            if (data.iconUrl) {
              const b64 = await downloadImageAsBase64(data.iconUrl);
              if (b64) updatedColors.iconUrlCached = b64;
            }
            if (data.faviconUrl) {
              const b64 = await downloadImageAsBase64(data.faviconUrl);
              if (b64) updatedColors.faviconUrlCached = b64;
            }
          } else {
            // Offline: preservar las imágenes cacheadas desde localStorage
            if (savedColors.iconUrlCached) updatedColors.iconUrlCached = savedColors.iconUrlCached;
            if (savedColors.faviconUrlCached) updatedColors.faviconUrlCached = savedColors.faviconUrlCached;
          }
          
          setColors(updatedColors);
          localStorage.setItem('cenit-appearance-colors', JSON.stringify(updatedColors));
          updatePageTitle((data.title !== null && data.title !== "") ? data.title : t('login.titleApp'));
        }
        else {
          updatePageTitle(t('login.titleApp'));
        }
      } else {
        updatePageTitle(t('login.titleApp'));
      }
    } catch (error) {
      updatePageTitle(t('login.titleApp'));
    } finally {
      renderContent();
    }
  }, [t])

  const memoizedColorsValue = React.useMemo(() => ({
    colors,
    setColors,
    updateColors,
    resetColors,
    openBackDrop,
    contentRender,
    flagNotification,
    setFlagNotification,
  }), [colors, updateColors, resetColors]);

  useEffect(() => {
    if (!actionRef.current) {
        getAppearance()
        actionRef.current = true
    } else {
      if (window.location.pathname === Router.appLogin) {
        if (!actionRef.current) {
          getAppearance(); // Llamar a getAppearance al ingresar al login
        }
      }
    }

  }, [getAppearance])
  

  return (
    <ColorsContext.Provider value={memoizedColorsValue}>
      {children}
    </ColorsContext.Provider>
  );
};

const useColors = () => {
    return React.useContext(ColorsContext);
};

export { ColorsProvider, ColorsContext, useColors };
